import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getCashFlow,
  getCashFlowSettings,
  resetCashFlowSettings,
  updateMonthlyCashFlowSettings,
} from '~apis/cash-flow/getCashFlow';
import * as queryKeys from '~constants/query-keys';
import { queryClient } from '~libs/queryClient';
import { CashFlowRequest, MonthlyCashFlowSetting } from '~models/cashFlow';

export const CASH_FLOW_KEY = {
  default: ['cash-flow'],
  request: ({
    companyId,
    startDate,
    endDate,
    rangeType,
    fxRuleType,
    cardProjectionType,
  }: CashFlowRequest) => [
    ...CASH_FLOW_KEY.default,
    companyId,
    startDate,
    endDate,
    rangeType,
    fxRuleType,
    cardProjectionType,
  ],
};

export const CASH_FLOW_SOURCES_KEY = {
  default: ['cash-flow-sources'],
  request: ({ companyId, startDate, endDate, rangeType, fxRuleType }: CashFlowRequest) => [
    'cash-flow-sources',
    companyId,
    startDate,
    endDate,
    rangeType,
    fxRuleType,
  ],
};

export const useGetCashFlow = (request: CashFlowRequest) => {
  return useQuery(CASH_FLOW_KEY.request(request), () => getCashFlow(request), {
    enabled: !!request.companyId,
    refetchOnMount: true,
  });
};

export function useGetCashFlowSettings(companyId: string, userId: string) {
  return useQuery(
    [queryKeys.CASH_FLOW_SETTINGS().all, companyId, userId],
    () => getCashFlowSettings({ companyId, userId }),
    {
      enabled: !!companyId && !!userId,
      refetchOnMount: true,
    },
  );
}

export function useUpdateMonthlyCashFlowSettings() {
  return useMutation({
    mutationFn: (data: { companyId: string; userId: string; data: MonthlyCashFlowSetting }) =>
      updateMonthlyCashFlowSettings(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries([
        queryKeys.CASH_FLOW_SETTINGS().all,
        variables.companyId,
        variables.userId,
      ]);
    },
  });
}

export function useResetMonthlyCashFlowSettings() {
  return useMutation({
    mutationFn: (data: { companyId: string; userId: string }) => resetCashFlowSettings(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries([
        queryKeys.CASH_FLOW_SETTINGS().all,
        variables.companyId,
        variables.userId,
      ]);
    },
  });
}
